import React, { useEffect, useRef, useState } from "react";
import "./Services.css";

const SERVICES = [
  {
    icon: "📄",
    title: "Resume Enhancement",
    desc: "ATS-optimised, recruiter-ready resumes with one-on-one consultation to reflect your unique professional story.",
    outcome: "A clear, credible resume that gives recruiters a reason to call.",
    details: ["One-to-one career and experience review", "ATS-focused structure and keyword strategy", "Achievement-led content that reflects your impact"],
    color: "rgba(157,78,221,0.15)",
  },
  {
    icon: "🔗",
    title: "LinkedIn Optimization",
    desc: "Keyword-rich profiles that get discovered by top recruiters. We rewrite your headline, summary, and experience.",
    outcome: "A profile that is easier to find and stronger when it is found.",
    details: ["Search-ready headline and keyword map", "A compelling About section in your voice", "Experience sections shaped for recruiter scans"],
    color: "rgba(91,24,154,0.15)",
  },
  {
    icon: "🧭",
    title: "Career Counselling",
    desc: "Personalised roadmaps by experienced advisors — clarify your path and act on it with laser focus and confidence.",
    outcome: "A practical next-step plan built around the career you actually want.",
    details: ["Career direction and opportunity assessment", "Role, industry, and skills-gap guidance", "An actionable roadmap with focused priorities"],
    color: "rgba(199,125,255,0.1)",
  },
  {
    icon: "💡",
    title: "Technical Training",
    desc: "Industry-aligned, hands-on sessions tailored to your target role. Build real-world competence that impresses.",
    outcome: "The confidence to show up prepared for the work—and the interview.",
    details: ["Role-aligned learning plan", "Hands-on concepts and real-world scenarios", "Feedback to strengthen your technical narrative"],
    color: "rgba(60,9,108,0.2)",
  },
  {
    icon: "🎯",
    title: "Interview Preparation",
    desc: "Mock sessions, common-question coaching, and salary negotiation strategy — walk into every interview prepared.",
    outcome: "A composed, memorable interview performance from first answer to final follow-up.",
    details: ["Mock interviews with practical feedback", "Behavioural and role-specific answer coaching", "Offer, salary, and negotiation preparation"],
    color: "rgba(157,78,221,0.12)",
  },
  {
    icon: "🏢",
    title: "Job Placement",
    desc: "Direct access to our curated employer network spanning IT, finance, healthcare, and operations. Your role is waiting.",
    outcome: "A more supported route from your profile to the right opportunity.",
    details: ["Profile matching with relevant opportunities", "Guidance throughout the application process", "Support through offers and onboarding"],
    color: "rgba(91,24,154,0.18)",
  },
];

const Services = () => {
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedService) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedService(null);
    };

    document.body.classList.add("modal-open");
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedService]);

  const openService = (service, event) => {
    triggerRef.current = event.currentTarget;
    setSelectedService(service);
  };

  const closeService = () => {
    setSelectedService(null);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const startConversation = () => {
    closeService();
    window.setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  return (
    <section className="services" id="services" ref={ref}>
      <div className="services__header reveal">
        <span className="eyebrow">What We Offer</span>
        <h2 className="services__h2">End-to-End Career Solutions</h2>
        <p className="services__sub">
          Every service is designed to remove the friction between where you are
          and where you want to be.
        </p>
      </div>

      <div className="services__grid">
        {SERVICES.map((s, i) => (
          <button
            type="button"
            className="services__card glass glass-hover reveal"
            style={{ transitionDelay: `${i * 0.07}s`, "--card-color": s.color }}
            key={i}
            onClick={(event) => openService(s, event)}
            aria-haspopup="dialog"
            aria-label={`Learn more about ${s.title}`}
          >
            <div className="services__icon-wrap">
              <span className="services__icon">{s.icon}</span>
              <div className="services__icon-glow" style={{ background: s.color }} />
            </div>
            <h3 className="services__title">{s.title}</h3>
            <p className="services__desc">{s.desc}</p>
            <span className="services__arrow">View details <b>→</b></span>
          </button>
        ))}
      </div>

      {selectedService && (
        <div className="service-modal" role="presentation" onMouseDown={closeService}>
          <section
            className="service-modal__dialog glass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button type="button" className="service-modal__close" onClick={closeService} aria-label="Close details">
              <span />
              <span />
            </button>

            <div className="service-modal__hero">
              <div className="service-modal__icon">{selectedService.icon}</div>
              <div>
                <span className="eyebrow">Placify Service</span>
                <h3 id="service-modal-title">{selectedService.title}</h3>
              </div>
            </div>

            <p className="service-modal__intro">{selectedService.desc}</p>

            <div className="service-modal__details">
              <span className="service-modal__label">What’s included</span>
              <ul>
                {selectedService.details.map((detail) => <li key={detail}>{detail}</li>)}
              </ul>
            </div>

            <div className="service-modal__outcome">
              <span>THE OUTCOME</span>
              <p>{selectedService.outcome}</p>
            </div>

            <button type="button" className="service-modal__cta" onClick={startConversation}>
              Start a conversation <span>→</span>
            </button>
          </section>
        </div>
      )}
    </section>
  );
};

export default Services;
